import type { ReactNode } from 'react';
import { Box, Flex, Loader, Text, UnstyledButton } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MeetingPlatformIcon } from 'src/meetings/components/MeetingPlatformIcon';
import {
  MeetingNoteStatus,
  useCalendarEventMeetingNote,
} from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { TranscriptionStatus } from 'src/meetings/detail/tabs/transcript/types';
import { formatEventTime, formatMeetingDuration, isPastEvent } from 'src/meetings/utils/date-utils';
import { getClientDisplayNames, getParticipantDisplayNames } from 'src/meetings/utils/event-utils';
import { useClientFamilyMap } from 'src/clients/hooks/useClientFamilyMap';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import { ParticipantPill } from './ParticipantPill';

type Props = {
  meeting: FamilyMeeting;
  showClientLabel?: boolean;
  onOpenParticipants?: (eventId: string) => void;
  onOpenDetails?: (eventId: string) => void;
  onGenerateMeetingNote?: (eventId: string) => void;
  onAssignSpeaker?: (eventId: string, speakerLabel: string) => void;
};

export function MeetingCard({
  meeting,
  showClientLabel = false,
  onOpenParticipants,
  onOpenDetails,
  onGenerateMeetingNote,
  onAssignSpeaker,
}: Props) {
  const clientFamilyMap = useClientFamilyMap();
  const participantNames = getParticipantDisplayNames(meeting, clientFamilyMap);
  const clientNames = getClientDisplayNames(meeting);
  const clientLabel =
    clientNames.length > 0 ? clientNames.join(', ') : 'No client linked to this meeting';
  const isPast = isPastEvent(meeting);

  // The meetings list carries each event's transcript summary, so a card needs no
  // per-event transcript fetch to know its status or unidentified speakers.
  const transcriptionStatus = meeting.transcriptionStatus;
  const transcriptReady = transcriptionStatus === TranscriptionStatus.READY;
  const { meetingNote } = useCalendarEventMeetingNote(meeting.id, {
    enabled: isPast && !!meeting.hasTranscription && transcriptReady,
  });
  const noteExistsOrGenerating =
    meetingNote?.status === MeetingNoteStatus.READY ||
    meetingNote?.status === MeetingNoteStatus.MEETING_NOTE_PROCESSING;
  const unidentifiedSpeakerCount = transcriptReady ? meeting.unidentifiedSpeakerCount : 0;
  const firstUnidentifiedSpeakerLabel = meeting.firstUnidentifiedSpeakerLabel ?? undefined;

  const renderNoteStatus = (): ReactNode => {
    if (isPast && meeting.hasTranscription) {
      if (transcriptReady) {
        // Once a note exists or is generating, drop the generate action.
        if (!noteExistsOrGenerating) {
          return (
            <UnstyledButton
              onClick={event => {
                event.stopPropagation();
                onGenerateMeetingNote?.(meeting.id);
              }}
            >
              <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="brand.6">
                Generate meeting note
              </Text>
            </UnstyledButton>
          );
        }
        if (meetingNote?.status === MeetingNoteStatus.MEETING_NOTE_PROCESSING) {
          return (
            <Flex align="center" gap="xxs">
              <Loader size={20} color="gray.5" />
              <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
                Generating meeting note...
              </Text>
            </Flex>
          );
        }
        return (
          <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
            Meeting note generated
          </Text>
        );
      }

      // A transcript exists but isn't ready yet — show processing while it's
      // actually being transcribed. A failed or empty transcript falls through
      // to the standard footer rather than spin forever.
      if (transcriptionStatus === TranscriptionStatus.PROCESSING) {
        return (
          <Flex align="center" gap="xxs">
            <Loader size={20} color="gray.5" />
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
              Processing transcript...
            </Text>
          </Flex>
        );
      }
    }

    // Upcoming meetings, past meetings with no transcript, or a failed/empty
    // transcript all fall back to the standard recording footer.
    return (
      <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
        Recording: Enabled
      </Text>
    );
  };

  // Unidentified speakers don't block note generation — the note reads better
  // once they're named, so nudge without gating.
  const showAssignPrompt =
    isPast && meeting.hasTranscription && transcriptReady && unidentifiedSpeakerCount > 0;

  return (
    <Box
      onClick={() => onOpenDetails?.(meeting.id)}
      style={{
        borderRadius: 20,
        border: '1px solid var(--mantine-color-gray-1)',
        overflow: 'hidden',
        cursor: onOpenDetails ? 'pointer' : undefined,
      }}
    >
      <Box px="md" pt="md" pb="lg">
        <Box>
          {showClientLabel && (
            <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.5" mb="xxxs">
              {clientLabel}
            </Text>
          )}
          <Text size="textMd" fw={FONT_WEIGHTS.medium} c="gray.10">
            {meeting.title}
          </Text>
          <Flex align="center" gap="xxxs" mt="xxxs">
            {meeting.meetingPlatform && (
              <MeetingPlatformIcon platform={meeting.meetingPlatform} size={16} />
            )}
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.5">
              {formatTimeWithDuration(meeting)}
            </Text>
          </Flex>
        </Box>

        {participantNames.length > 0 && (
          <Box mt="sm" w="fit-content" onClick={event => event.stopPropagation()}>
            <ParticipantPill
              participantNames={participantNames}
              onOpen={onOpenParticipants ? () => onOpenParticipants(meeting.id) : undefined}
            />
          </Box>
        )}
      </Box>

      <Box
        px="md"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-1)',
          paddingTop: 13,
          paddingBottom: 17,
        }}
      >
        {renderNoteStatus()}
        {showAssignPrompt && (
          <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6" mt="xxs">
            {unidentifiedSpeakerCount} speaker{unidentifiedSpeakerCount === 1 ? '' : 's'}{' '}
            unidentified.{' '}
            <Text
              span
              inherit
              c="brand.7"
              style={{ cursor: 'pointer' }}
              onClick={event => {
                event.stopPropagation();
                if (firstUnidentifiedSpeakerLabel)
                  onAssignSpeaker?.(meeting.id, firstUnidentifiedSpeakerLabel);
              }}
            >
              Assign speakers
            </Text>{' '}
            for a more accurate note.
          </Text>
        )}
      </Box>
    </Box>
  );
}

function formatTimeWithDuration(meeting: FamilyMeeting): string {
  const start = meeting.startAt ?? meeting.scheduledStartAt;
  const end = meeting.endAt ?? meeting.scheduledEndAt;
  const range = formatEventTime(start, end).replace(/ (AM|PM)/g, '$1');
  return `${range} (${formatMeetingDuration(start, end)})`;
}
